/* eslint-disable no-use-before-define */
import React, {useState} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {SEARCH_DEBOUNCE_DELAY} from '@shared/constants/Mapping';
import {useDebouncedCallback} from 'use-debounce';
import { Checkbox, CircularProgress, makeStyles, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AutocompleteProps } from '@material-ui/lab/Autocomplete';
import { DEFAULT_RED_COLOR, INPUT_BORDER_COLOR, INPUT_LABEL_COLOR } from 'src/style/variable';
import ChronicIcon from '../MyIcons/ChronicIcon';

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
interface ProblemType {
  label: any;
  problem: string;
}
interface IMultipleAutocompleteInputProps<T>
  extends Omit<WithOptional<
    AutocompleteProps<T, undefined, undefined, undefined>,
    'options' | 'renderInput'
  >, 'onChange' | 'value'> {
  fetchOptions?: (searchedText: string) => Promise<T[] | undefined>;
  label: string;
  error?: Record<string, any>;
  value?: T[];
  onChange?: (value: T[]) => void;
  disableCloseOnSelect?: any;
  openOnFocus?: any;
  gridDataLoaded?: any;
}
const useStyles = makeStyles(() => ({
  autoCompleteInput: {
    '& .MuiAutocomplete-clearIndicator': {
      display: 'none !important',
      visibility: 'hidden',
      width: 0,
      height: 0,
      padding: 0,
      margin: 0,
      pointerEvents: 'none',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'none',
    },
    '& .MuiInputLabel-formControl': {
      position: 'static',
      color: INPUT_LABEL_COLOR,
      fontSize: '1.1rem',
      marginBottom: '0.825rem',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none',
    },
    '& .MuiInputBase-input': {
      marginTop: '-0.5rem',
    },
    '& .MuiFormControl-root': {
      paddingBottom: 0,
      marginBottom: 0,
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: INPUT_BORDER_COLOR,
    },
    '& .MuiChip-labelSmall': {
     maxWidth: '45rem',
    }
  },
  chronicClass: {
    color: DEFAULT_RED_COLOR,
    fontSize: 28,
    // marginLeft: '1rem'
  }
}));
const MultipleAutoSelectForCCMProblems = <T extends ProblemType> (
  props: IMultipleAutocompleteInputProps<T>
): JSX.Element => {
  const {
    fetchOptions,
    label,
    placeholder,
    error,
    className,
    options: optionsList,
    onChange,
    value = [] as T[],
    disableCloseOnSelect,
    openOnFocus,
    gridDataLoaded
  } = props;
  const classes = useStyles();
  const [options, setOptions] = React.useState<T[]>(
    Array.isArray(optionsList) ? optionsList : [] // Ensure options is always an array
  );
  const [enteredValue, setEnteredValue] = React.useState('');
  const [loading, setLoading] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const updateOptions = async () => {
    if (!fetchOptions) return;
    try {
      setLoading(true);
      const filteredOptions = await fetchOptions(enteredValue);
      setOptions(filteredOptions);

    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    } finally {
      setLoading(false);
    }
  };
  const {t} = useTranslation();

  const debouncedFetch = useDebouncedCallback(
    () => updateOptions(),
    SEARCH_DEBOUNCE_DELAY
  );

  React.useEffect(() => {
    if (!fetchOptions) return;
    if(gridDataLoaded) {
      debouncedFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enteredValue, debouncedFetch,gridDataLoaded]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputChange = (event: any, newValue: string, reason: string) => {
    if (reason !== "reset") {
      setEnteredValue(newValue); // Update input field with typed text
    }
  };

  const handleSelectChange = (event: any, newValue: any) => {
    if (onChange) {
      if (enteredValue?.length > 0) {
        // setSelectedValues(newValue);
        onChange(value);
      } else {
        // Ensure all previous selections remain even when search text is cleared
        const mergedValues = [...newValue, ...value].filter(
          (item, index, self) => index === self.findIndex((data: any) => data.id === item.id)
        );
        onChange(mergedValues);
      }
    }
  };
  const handleOpen = async () => {
    if (fetchOptions) {
      setLoading(true);
      try {
        const searchText = enteredValue?.trim() ? enteredValue : ''; // Use enteredValue if present
        const allOptions = await fetchOptions(searchText);
        setOptions(allOptions || []);
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <Autocomplete
      multiple
      fullWidth
      id="tags-standard"
      size="small"
      loading={loading}
      options={options}
      value={value}
      onOpen={handleOpen}
      disableCloseOnSelect={disableCloseOnSelect} //  Prevent closing when selecting an option
      openOnFocus={openOnFocus} //  Open dropdown when clicking inside
      className={`${classes.autoCompleteInput} ${className}`}
      getOptionLabel={(option: T) => (option as any).label}
      onChange={handleSelectChange} // Handle selection change
      onInputChange={(event, newValue, reason) => {
        handleInputChange(event, newValue, reason);
      }}
      renderTags={() => null} // **This removes the chips from the input**

      renderOption={(option, { selected }) => {
        const isChecked = !!value.find((item: any) => item.id === option.id);
       return (
            <div
              onClick={(e) => {
                 if (!option.disabled) {
                   e.stopPropagation(); // Prevent dropdown from closing
                    const updatedSelection = isChecked
                    ? value.filter((item: any) => item.id !== option.id) // Remove if already selected
                     : [...value, option]; // Add if not selected

                   onChange(updatedSelection);
                 }
              }}
                  style={{
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "space-between",
                   width: "100%",
                   opacity: option.disabled ? 0.5 : 1,
                   cursor: option.disabled ? "auto" : "pointer",
                    }}
                 >
                  <div style={{ display: "flex", alignItems: "center" }}>
                     <Checkbox
                       style={{ marginRight: 8 }}
                       checked={isChecked}
                       disabled={option.disabled}
                      />
                    {option?.label}
                  </div>
                 {option.is_chronic && (
                  <Tooltip title={t("CCM.CHRONIC")} placement="top">
                   <div>
                     <ChronicIcon className={classes.chronicClass} />
                   </div>
                  </Tooltip>
                  )}
            </div>
              );
          }}

      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          value={enteredValue} // Ensures input text persists
          onChange={(e) => setEnteredValue(e.target.value)} // Updates field
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message ? t(error.message) : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          inputProps={{
            ...params.inputProps,
            value: enteredValue
          }}
          InputLabelProps={{
            shrink: true
          }}
        />
      )}
    />
  );
};

export default MultipleAutoSelectForCCMProblems;
